import { useEffect, useState } from "react";
import { api, fmt, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = ["youtube", "press", "instagram", "bundles", "viral", "deals"];
const SECTIONS = [{ v: "", l: "—" }, { v: "music_entertainment", l: "Music + Entertainment" }, { v: "top_tier", l: "Top Tier Media" }, { v: "seo", l: "Press + Google Ranking" }];
const EMPTY = { platform: "youtube", name: "", price: 0, unit: "one-time", description: "", checkout_type: "buy_now", deposit_amount: 0, sale_price: 0, section: "", level: "", active: true };

export default function MarketingAdmin() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = () => api.get("/admin/marketing-services").then((r) => setServices(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, price: Number(editing.price) || 0, deposit_amount: Number(editing.deposit_amount) || 0, sale_price: Number(editing.sale_price) || 0 };
      if (editing.id) await api.put(`/admin/marketing-services/${editing.id}`, payload);
      else await api.post("/admin/marketing-services", payload);
      toast.success(editing.id ? "Service updated." : "Service created.");
      setEditing(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/marketing-services/${id}`);
      toast.success("Service deleted.");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-marketing">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-white">Marketing Services</h1>
        <button data-testid="add-service-btn" onClick={() => setEditing({ ...EMPTY })}
          className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-5 py-3 transition-colors">
          <Plus className="w-4 h-4" /> Add Service
        </button>
      </div>
      {PLATFORMS.map((p) => {
        const group = services.filter((s) => s.platform === p);
        if (!group.length) return null;
        return (
          <div key={p} className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.3em] text-brand mb-3">{p}</h2>
            <div className="border border-white/10 divide-y divide-white/5">
              {group.map((s) => (
                <div key={s.id} data-testid={`admin-service-${s.id}`} className="bg-[#0A0A0A] p-4 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-white font-medium">{s.name} {!s.active && <span className="text-[10px] uppercase text-zinc-500 ml-2">Hidden</span>}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {fmt(s.price)}{s.unit === "monthly" ? "/mo" : ""}
                      {s.checkout_type === "deposit_approval" ? ` · deposit ${fmt(s.deposit_amount)} + approval` : " · buy now"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`edit-service-${s.id}`} onClick={() => setEditing({ ...s })}
                      className="flex items-center gap-1.5 text-xs text-zinc-300 border border-white/15 hover:border-brand px-3 py-2 transition-colors">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button data-testid={`delete-service-${s.id}`} onClick={() => remove(s.id)}
                      className="flex items-center gap-1.5 text-xs text-red-400 border border-white/15 hover:border-red-400 px-3 py-2 transition-colors">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="service-dialog" className="bg-[#0A0A0A] border-white/10 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">{editing?.id ? "Edit Service" : "New Service"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-400">Platform</Label>
                <Select value={editing.platform} onValueChange={(v) => setEditing({ ...editing, platform: v })}>
                  <SelectTrigger data-testid="service-platform" className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Name *</Label>
                <Input data-testid="service-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Description</Label>
                <Textarea data-testid="service-description" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-400">Price (USD)</Label>
                  <Input data-testid="service-price" type="number" min="0" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Billing</Label>
                  <Select value={editing.unit} onValueChange={(v) => setEditing({ ...editing, unit: v })}>
                    <SelectTrigger data-testid="service-unit" className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="one-time">One-time</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-400">Checkout type</Label>
                  <Select value={editing.checkout_type} onValueChange={(v) => setEditing({ ...editing, checkout_type: v })}>
                    <SelectTrigger data-testid="service-checkout-type" className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="buy_now">Buy now (full price)</SelectItem>
                      <SelectItem value="deposit_approval">Deposit + approval</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editing.checkout_type === "deposit_approval" && (
                  <div>
                    <Label className="text-xs text-zinc-400">Deposit amount</Label>
                    <Input data-testid="service-deposit" type="number" min="0" step="0.01" value={editing.deposit_amount} onChange={(e) => setEditing({ ...editing, deposit_amount: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-zinc-400">Sale price (optional)</Label>
                  <Input data-testid="service-sale-price" type="number" min="0" step="0.01" value={editing.sale_price || 0} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
                </div>
                {editing.platform === "press" && (
                  <>
                    <div>
                      <Label className="text-xs text-zinc-400">Press section</Label>
                      <Select value={editing.section || "none"} onValueChange={(v) => setEditing({ ...editing, section: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="service-section" className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          {SECTIONS.map((s) => <SelectItem key={s.v || "none"} value={s.v || "none"}>{s.l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-400">Level (1-4)</Label>
                      <Input data-testid="service-level" value={editing.level || ""} onChange={(e) => setEditing({ ...editing, level: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="service-active" checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label className="text-xs text-zinc-400">Visible on site</Label>
              </div>
              <button data-testid="service-save-btn" type="submit"
                className="w-full bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase py-3 transition-colors">
                Save Service
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
